<template>
    <div>
        <!-- When user is viewing a page for deleted items -->
        <div v-if="isDeleteView">
            <a v-if="item.userCanRestore" class="btn btn-default btn-block" :href="restoreItemEndPoint">
                <span class="fa fa-undo" aria-hidden="true" /> Restore item
            </a>
            <a v-else class="btn btn-default btn-block table-browser-is-disabled" disabled
               data-toggle="tooltip" data-placement="top" title="You do not have permission to restore this item"
            >
                <span class="fa fa-undo" aria-hidden="true" /> Restore item
            </a>
        </div>

        <!-- When user is viewing a page for non deleted items -->
        <div v-else>
            <!-- Modal when user is removing item from a workgroup -->
            <modal v-model="removeFromWorkGroupModelOpened">
                <span slot="title" class="margin-left margin-right">
                    <em>Removing "{{ item.name }}"</em>
                </span>
                <api-errors :errors="Errors" />
                <div class="alert alert-warning">
                    <div class="word-break-all">
                        <strong>
                            You are about to remove "{{ item.name }}" from "{{ extraInfo.workgroupName }}"
                        </strong>
                    </div>
                    <div class="margin-above">
                        Click "Confirm" to proceed.
                    </div>
                </div>
                <span slot="footer">
                        <button type="button" class="btn btn-default" @click="toggleRemoveFromWorkGroupModel">
                            <span class="fa fa-times" aria-hidden="true" /> Cancel
                        </button>

                        <submit-button
                            text="Confirm"
                            icon="chain-broken"
                            type="primary"
                            :loading="isLoading"
                            @click="removeFromWorkGroup"
                        />
                    </span>
            </modal>

            <!-- When user can perform an item action -->
            <button v-if="item.userCanEdit || item.userCanDelete"
                    type="button" class="btn btn-default" @click="toggleDropdown"
            >
                Item actions <span class="caret" />
            </button>

            <!-- When user cannot perform an item action -->
            <button v-else
                    type="button" class="btn btn-default table-browser-is-disabled" disabled
                    data-toggle="tooltip" data-placement="top"
                    title="You do not have permission to perform any item actions"
            >
                Item actions <span class="caret" />
            </button>

            <!-- Item action options -->
            <transition name="table-browser-slide">
                <ul class="table-browser-dropdown-menu" v-if="dropDownOpened">
                    <!-- When user can delete item -->
                    <li v-if="item.userCanDelete">
                        <a :href="deleteItemEndPoint" class="btn btn-default btn-block">
                         <span class="glyphicon glyphicon-trash" aria-hidden="true" /> Delete item
                        </a>
                    </li>

                    <!-- When user can not delete item -->
                    <li v-else>
                        <a class="btn btn-default btn-block table-browser-is-disabled" disabled
                           data-toggle="tooltip" data-placement="top"
                           title="You do not have permission to delete this item"
                        >
                         <span class="glyphicon glyphicon-trash" aria-hidden="true" /> Delete item
                        </a>
                    </li>

                    <!-- When user can edit item -->
                    <template v-if="item.userCanEdit">
                        <li>
                            <a :href="editItemEndPoint" class="btn btn-default btn-block">
                                <span class="fa fa-pencil" aria-hidden="true" /> Edit item
                            </a>
                        </li>
                        <li v-if="isWorkgroupView">
                            <a @click="toggleRemoveFromWorkGroupModel" class="btn btn-default btn-block">
                                <span class="fa fa-chain-broken" /> Remove from workgroup
                            </a>
                        </li>
                    </template>

                    <!-- When user can not edit item -->
                    <template v-else>
                        <li>
                            <a class="btn btn-default btn-block table-browser-is-disabled" disabled
                               data-toggle="tooltip" data-placement="top"
                               title="You do not have permission to edit this item"
                            >
                                <span class="fa fa-pencil" /> Edit item
                            </a>
                        </li>
                        <li v-if="isWorkgroupView">
                            <a class="btn btn-default btn-block table-browser-is-disabled" disabled
                               data-toggle="tooltip" data-placement="top"
                               title="You do not have permission to remove this item"
                            >
                                <span class="fa fa-chain-broken" /> Remove from workgroup
                            </a>
                        </li>
                    </template>
                </ul>
            </transition>
        </div>
    </div>
</template>

<script>
import Modal from 'uiv/src/components/modal/Modal.vue'
import apiRequest from 'src/mixins/api-request.js'
import apiErrors from '@/ApiErrorDisplay.vue'
import SubmitButton from '@/SubmitButton.vue'

export default {
    name: "ItemAction",
    components: {
        Modal,
        apiErrors,
        SubmitButton,
    },
    mixins: [apiRequest],
    props: {
        item: {
            type: Object,
            required: true
        },

        extraInfo: {
            type: Object,
            default: () => ({})
        },

        isDeleteView: {
            type: Boolean,
            required: true,
        },
    },

    data: () => ({
        removeFromWorkGroupModelOpened: false,
        Errors: {},
        isLoading: false,
        dropDownOpened: false,
    }),

    computed: {
        isWorkgroupView() {
            return Object.prototype.hasOwnProperty.call(this.extraInfo, 'workgroupId')
                && Object.prototype.hasOwnProperty.call(this.extraInfo, 'workgroupName')
        },

        restoreItemEndPoint() {
            if (Object.prototype.hasOwnProperty.call(this.extraInfo, 'restoreEndPointWithRedirect')) {
                return this.extraInfo.restoreEndPointWithRedirect
                    .replace('{iid}', this.item.aristotleId)
                    .replace('{path}', location.href)
            }
            return ''
        },

        deleteItemEndPoint() {
            if (Object.prototype.hasOwnProperty.call(this.extraInfo, 'deleteEndPointWithRedirect')) {
                return this.extraInfo.deleteEndPointWithRedirect
                    .replace('{iid}', this.item.aristotleId)
                    .replace('{path}', location.href)
            }
            return ''
        },

        editItemEndPoint() {
            if (Object.prototype.hasOwnProperty.call(this.extraInfo, 'editEndPointWithRedirect')) {
                return this.extraInfo.editEndPointWithRedirect
                    .replace('{iid}', this.item.aristotleId)
                    .replace('{path}', location.href)
            }
            return ''
        },

        removeFromWorkgroupEndPoint() {
            if (Object.prototype.hasOwnProperty.call(this.extraInfo, 'editApiEndPoint')) {
                return this.extraInfo.editApiEndPoint
                    .replace('{metadataType}', this.getFlatCase(this.item.metadataTypeName))
                    .replace('{metadataUuid}', this.item.uuid)
            }
            return ''
        }

    },

    mounted() {
        document.addEventListener('click', this.closeDropdown)
    },

    beforeDestroy() {
        document.removeEventListener('click', this.closeDropdown)
    },

    methods: {
        toggleRemoveFromWorkGroupModel: function () {
            this.removeFromWorkGroupModelOpened = !this.removeFromWorkGroupModelOpened
        },

        toggleDropdown: function () {
            this.dropDownOpened = !this.dropDownOpened
        },

        // Used to close dropdown when click outside of the dropdown menu
        closeDropdown(event) {
            if (!this.$el.contains(event.target)) {
                this.dropDownOpened = false
            }
        },

        // Need to flatten the metadata type name to access a specific v4 metadata api.
        // From example, flat case of "Conceptual Domain" is "conceptualdomain"
        getFlatCase: function (string) {
            return string.toLowerCase().split(" ").join("")
        },

        removeFromWorkGroup() {
            this.isLoading = true
            this.patch(
                this.removeFromWorkgroupEndPoint,
                {
                    'change_comment': 'Removed from ' + this.extraInfo.workgroupName,
                    'workgroup': '',
                }
            ).then(() => {
                this.toggleRemoveFromWorkGroupModel()
                this.$emit('removed-from-workgroup', this.item)
            }).catch((error) => {
                this.errors = error.response.data
            }).finally(() => {
                this.isLoading = false
            })
        },
    }
}
</script>
